import { logger } from '@adornis/base/logging.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import type { XTextarea } from '@adornis/forms/x-textarea.js';
import '@chancenlogistik/client/components/logistik-component-base-header';
import '@chancenlogistik/client/components/logistik-component-base-page-layout';
import '@chancenlogistik/client/components/logistik-component-shipping-details';
import type { LogistikComponentShippingDetails } from '@chancenlogistik/client/components/logistik-component-shipping-details';
import { LogistikRoutes } from '@chancenlogistik/client/routes';
import { LogistikEntireTrip, LogistikOrder, LogistikOrderShippingDetails } from '@chancenwerk/db/LogistikOrder.js';
import {
  LogistikProductBahncard,
  LogistikShippingRecipient,
  LogistikTripAge,
} from '@chancenwerk/db/enums/LogistikEnums.js';
import type { ChromiumButton } from '@chancenwerk/design/components/cr-button.js';
import { ChromiumDialog } from '@chancenwerk/design/components/cr-dialog.js';
import '@chancenwerk/design/components/cr-radiobutton.js';
import '@chancenwerk/design/components/cr-textarea.js';
import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { createRef, ref, type Ref } from 'lit/directives/ref.js';
import { repeat } from 'lit/directives/repeat.js';

const createDefaultOrder = () =>
  new LogistikOrder({
    shippingDetails: new LogistikOrderShippingDetails({
      shippingRecipient: LogistikShippingRecipient.SINGLE_PERSON,
    }),
    items: [new LogistikEntireTrip({})],
  });

/**
 * @element logistik-page-entire-trip
 */
@customElement('logistik-page-entire-trip')
export class LogistikPageEntireTrip extends ChemistryLitElement {
  private newOrder = createDefaultOrder();

  @state() private readonly _form = new FormController(this, this.newOrder, {
    success: async order => {
      try {
        await ChromiumDialog.waitFor(order.create());
        void ChromiumDialog.alert('Bestellung für eine Fahrt und Unterkunft wurde erfolgreich aufgegeben.');
        this.newOrder = createDefaultOrder();
        this._form.document = this.newOrder;
        this._shippingRef.value?.clearSelection();
        this.requestUpdate();
      } catch (e) {
        logger.error({ err: e }, 'There was an error completing entire trip order.');
        ChromiumDialog.alert('Es gab ein Problem beim Aufgeben der Bestellung: ' + (e as Error).message);
      }
    },
  });
  @state() private readonly _shippingRef = createRef<LogistikComponentShippingDetails>();

  private _reasonRef: Ref<XTextarea> = createRef();
  private _descriptionRef: Ref<XTextarea> = createRef();

  /**
   * Helper to open the textarea by default
   * @param ref - the ref to the textarea
   * @returns void
   */
  private _openTextarea(ref: Ref<XTextarea>) {
    if (!ref) {
      return;
    }

    requestAnimationFrame(() => {
      const area = ref.value?.shadowRoot?.querySelector('cr-formfield-wrapper')?.querySelector('textarea');
      if (area) {
        area.style.height = 'auto';
      }
    });
  }

  override firstUpdated(arg): void {
    super.updated(arg);

    this._openTextarea(this._reasonRef);
    this._openTextarea(this._descriptionRef);
  }

  override render() {
    return html`
      <logistik-component-base-page-layout>
        <logistik-component-base-header
          pageTitle="Fahrt und Unterkunft"
          backRoute=${LogistikRoutes.landing}
        ></logistik-component-base-header>
        <x-flex space="lg">
          <cr-text> Hier können Sie eine Fahrt und Unterkunft buchen. </cr-text>
          <x-flex space="md">
            <cr-textarea
              placeholder="Grund für die Reise"
              rows="5"
              ${ref(this._reasonRef)}
              ${this._form.field('items', 0, 'reason')}
            >
            </cr-textarea>
            <cr-flex
              horizontal
              space="md"
              padding="sm"
              ${css({
                borderLeft: `5px solid ${this.colors.primary}`,
                backgroundColor: this.colors.neutral.shade(100),
              })}
            >
              <cr-icon>info</cr-icon>
              <cr-text>
                Bitte geben Sie hier die gewünschten Transportmittel und Fahrtzeiten sowie Informationen zur gewünschten
                Unterkunft an. Bitte geben Sie auch an ob eine Barrierefreiheit notwendig ist.
              </cr-text>
            </cr-flex>
            <cr-textarea
              placeholder="Vorschlag zu Fahrt und Unterkunft"
              rows="5"
              ${ref(this._descriptionRef)}
              ${this._form.field('items', 0, 'description')}
            ></cr-textarea>
            <cr-dropdown-selection
              ${this._form.field('items', 0, 'ageAtDeparture')}
              placeholder="Angabe des Alters bei Fahrtantritt*"
              .selectables=${Object.values(LogistikTripAge)}
            >
            </cr-dropdown-selection>
            <cr-radiogroup ${this._form.field('items', 0, 'bahncard')}>
              <x-flex horizontal space="md">
                ${repeat(
                  Object.values(LogistikProductBahncard),
                  bahncard => html`
                    <cr-radiobutton selectable=${bahncard}>
                      <cr-text> ${bahncard} </cr-text>
                    </cr-radiobutton>
                  `,
                )}
              </x-flex>
            </cr-radiogroup>
            <logistik-component-shipping-details
              ${this._form.field('shippingDetails')}
              ${ref(this._shippingRef)}
            ></logistik-component-shipping-details>
          </x-flex>
          <cr-text tone="warning"> Lassen Sie sich bitte eine Rechnung aushändigen! </cr-text>
          <x-flex horizontal end space="md">
            <cr-button
              icon="send"
              @click=${async (e: MouseEvent) => {
                const currentTarget = e.currentTarget as ChromiumButton;
                try {
                  await this._shippingRef.value?._form.validate({ mouseEvent: e });
                  await this._form.validate({ mouseEvent: e });
                } catch {
                  currentTarget.loading = false;
                }
              }}
            >
              Bestellung aufgeben
            </cr-button>
          </x-flex>
        </x-flex>
      </logistik-component-base-page-layout>
    `;
  }
}
